import React, { Suspense, lazy } from 'react';
import { Hero } from './components/Hero';

/**
 * Everything below the first viewport is code-split so the Hero (LCP) ships
 * with the smallest possible JS payload. Each block reserves vertical space
 * while its chunk loads, which keeps CLS at zero.
 */
const PainSection = lazy(() => import('./components/PainSection').then(m => ({
  default: m.PainSection
})));
const PositioningSection = lazy(() => import('./components/PositioningSection').then(m => ({
  default: m.PositioningSection
})));
const JourneySection = lazy(() => import('./components/JourneySection').then(m => ({
  default: m.JourneySection
})));
const MethodSection = lazy(() => import('./components/MethodSection').then(m => ({
  default: m.MethodSection
})));
const TomosSection = lazy(() => import('./components/TomosSection').then(m => ({
  default: m.TomosSection
})));
const BonosSection = lazy(() => import('./components/BonosSection').then(m => ({
  default: m.BonosSection
})));
const BenefitsSection = lazy(() => import('./components/BenefitsSection').then(m => ({
  default: m.BenefitsSection
})));
const TransformationSection = lazy(() => import('./components/TransformationSection').then(m => ({
  default: m.TransformationSection
})));
const CoverageSection = lazy(() => import('./components/CoverageSection').then(m => ({
  default: m.CoverageSection
})));
const DelegationSection = lazy(() => import('./components/DelegationSection').then(m => ({
  default: m.DelegationSection
})));
const AudienceSection = lazy(() => import('./components/AudienceSection').then(m => ({
  default: m.AudienceSection
})));
const OfferSection = lazy(() => import('./components/OfferSection').then(m => ({
  default: m.OfferSection
})));
const GuaranteeSection = lazy(() => import('./components/GuaranteeSection').then(m => ({
  default: m.GuaranteeSection
})));
const FaqSection = lazy(() => import('./components/FaqSection').then(m => ({
  default: m.FaqSection
})));
const FinalCta = lazy(() => import('./components/FinalCta').then(m => ({
  default: m.FinalCta
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const StickyBuyBar = lazy(() => import('./components/StickyBuyBar').then(m => ({
  default: m.StickyBuyBar
})));
interface DeferredProps {
  children: React.ReactNode;
  minHeight: string;
  background?: string;
}
function Deferred({
  children,
  minHeight,
  background = 'bg-ivory'
}: DeferredProps) {
  return <Suspense fallback={<div className={`w-full ${background}`} style={{
    minHeight
  }} aria-hidden data-id="element-1" data-mp="3bnmfji6q357t" />} data-id="element-0" data-mp="3bn2zk70muqwf">
      {children}
    </Suspense>;
}
export function App() {
  return <main className="w-full bg-ivory text-charcoal" data-id="element-2" data-mp="3lkr6rwffdqvh">
      <Hero data-id="element-3" data-mp="3liwm8j82g0rx" />
      <Deferred minHeight="560px" data-id="element-4" data-mp="3liedin47vu34">
        <PainSection data-id="element-5" data-mp="2xun01g6xxb09" />
      </Deferred>
      <Deferred minHeight="560px" background="bg-sand" data-id="element-6" data-mp="2xtlo79i3okhh">
        <PositioningSection data-id="element-7" data-mp="2sw1yndfycla8" />
      </Deferred>
      <Deferred minHeight="520px" data-id="element-8" data-mp="2svafj23jkg0u">
        <JourneySection data-id="element-9" data-mp="18drjdvxkvfdt" />
      </Deferred>
      <Deferred minHeight="520px" background="bg-charcoal" data-id="element-10" data-mp="18cq5ljp6gese">
        <MethodSection data-id="element-11" data-mp="1d4hod823dg7z" />
      </Deferred>
      <Deferred minHeight="1100px" data-id="element-12" data-mp="1d5j8s34zzf2d">
        <TomosSection data-id="element-13" data-mp="1d6koikwykpqf" />
      </Deferred>
      <Deferred minHeight="620px" background="bg-sand" data-id="element-14" data-mp="yg3lix4odrrf">
        <BonosSection data-id="element-15" data-mp="ydik298ahdu7" />
      </Deferred>
      <Deferred minHeight="620px" background="bg-sand" data-id="element-16" data-mp="13e56jnlnw15j">
        <BenefitsSection data-id="element-17" data-mp="13clq9x009q3r" />
      </Deferred>
      <Deferred minHeight="520px" background="bg-charcoal" data-id="element-18" data-mp="1r3r0nwn6fq6o">
        <TransformationSection data-id="element-19" data-mp="1r2g0e6sxx0kb" />
      </Deferred>
      <Deferred minHeight="520px" data-id="element-20" data-mp="1w2araacptq5d">
        <CoverageSection data-id="element-21" data-mp="1w2kgvwtulvaj" />
      </Deferred>
      <Deferred minHeight="560px" background="bg-sand" data-id="element-22" data-mp="1i4417ge0cug9">
        <DelegationSection data-id="element-23" data-mp="1i3az4spzut4g" />
      </Deferred>
      <Deferred minHeight="560px" data-id="element-24" data-mp="1m4mttcnq64ji">
        <AudienceSection data-id="element-25" data-mp="1m3l6o9tfprlf" />
      </Deferred>
      <Deferred minHeight="520px" data-id="element-26" data-mp="6ofe8p0r7vfg">
        <FaqSection data-id="element-27" data-mp="6ncnch3flszj" />
      </Deferred>
      <Deferred minHeight="560px" background="bg-sand" data-id="element-28" data-mp="6qictxfz2wvs">
        <OfferSection data-id="element-29" data-mp="ar29z10jq0hx" />
      </Deferred>
      <Deferred minHeight="240px" background="bg-sand" data-id="element-30" data-mp="anwjpig6kskm">
        <GuaranteeSection data-id="element-31" data-mp="qxfbe0ehnln1" />
      </Deferred>
      <Deferred minHeight="420px" background="bg-sand" data-id="element-32" data-mp="qz8i1fp3q5v1">
        <FinalCta data-id="element-33" data-mp="m5dz4ehblux0" />
      </Deferred>
      <Deferred minHeight="380px" background="bg-sand" data-id="element-34" data-mp="m544uep8t1li">
        <Footer data-id="element-35" data-mp="h6knkrllgdzh" />
      </Deferred>
      <Suspense fallback={null} data-id="element-36" data-mp="h7lxgsqviufm">
        <StickyBuyBar data-id="element-37" data-mp="c89dqgvau3h9" />
      </Suspense>
    </main>;
}